import { Box, Container, Typography, Button, Stack } from '@mui/material';
import { motion } from 'framer-motion';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import MailOutlineIcon from '@mui/icons-material/MailOutline';

const FloatingShape = ({ size, color, top, left, delay, reverse }) => (
  <Box
    sx={{
      position: 'absolute',
      width: size,
      height: size,
      borderRadius: '50%',
      background: color,
      top,
      left,
      filter: 'blur(2px)',
      animation: `${reverse ? 'floatReverse' : 'float'} ${6 + delay}s ease-in-out infinite`,
      animationDelay: `${delay}s`,
      opacity: 0.6,
    }}
  />
);

const GlowOrb = ({ size, color, top, left, blur }) => (
  <Box
    sx={{
      position: 'absolute',
      width: size,
      height: size,
      borderRadius: '50%',
      background: color,
      top,
      left,
      filter: `blur(${blur || 80}px)`,
      opacity: 0.25,
      pointerEvents: 'none',
    }}
  />
);

const containerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.15 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, ease: [0.25, 0.46, 0.45, 0.94] },
  },
};

export default function Hero() {
  const handleScroll = (href) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <Box
      id='home'
      sx={{
        minHeight: '100vh',
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        background:
          'linear-gradient(135deg, #0F0F1A 0%, #12101E 50%, #0F1520 100%)',
      }}
    >
      {/* Background glow orbs */}
      <GlowOrb size={600} color='#7C3AED' top='-10%' left='-10%' blur={120} />
      <GlowOrb size={500} color='#F59E0B' top='60%' left='70%' blur={120} />
      <GlowOrb size={400} color='#06B6D4' top='20%' left='60%' blur={100} />

      {/* Floating geometric shapes */}
      <FloatingShape
        size={18}
        color='rgba(124,58,237,0.9)'
        top='15%'
        left='8%'
        delay={0}
      />
      <FloatingShape
        size={12}
        color='rgba(245,158,11,0.9)'
        top='25%'
        left='85%'
        delay={1}
        reverse
      />
      <FloatingShape
        size={22}
        color='rgba(6,182,212,0.8)'
        top='70%'
        left='10%'
        delay={2}
      />
      <FloatingShape
        size={10}
        color='rgba(124,58,237,0.8)'
        top='80%'
        left='80%'
        delay={0.5}
        reverse
      />
      <FloatingShape
        size={16}
        color='rgba(245,158,11,0.7)'
        top='45%'
        left='92%'
        delay={1.5}
      />
      <FloatingShape
        size={8}
        color='rgba(6,182,212,0.9)'
        top='60%'
        left='3%'
        delay={3}
        reverse
      />

      {/* Rotating ring */}
      <Box
        sx={{
          position: 'absolute',
          width: 400,
          height: 400,
          border: '1px solid rgba(124,58,237,0.15)',
          borderRadius: '50%',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          animation: 'rotate-slow 25s linear infinite',
          pointerEvents: 'none',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          width: 600,
          height: 600,
          border: '1px solid rgba(245,158,11,0.08)',
          borderRadius: '50%',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          animation: 'rotate-slow 40s linear infinite reverse',
          pointerEvents: 'none',
        }}
      />

      {/* Dot grid pattern */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          backgroundImage:
            'radial-gradient(rgba(255,255,255,0.06) 1px, transparent 1px)',
          backgroundSize: '40px 40px',
          pointerEvents: 'none',
        }}
      />

      <Container maxWidth='lg' sx={{ position: 'relative', zIndex: 2 }}>
        <motion.div
          variants={containerVariants}
          initial='hidden'
          animate='visible'
        >
          {/* Badge */}
          <motion.div variants={itemVariants}>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                px: 2,
                py: 0.75,
                borderRadius: 50,
                background: 'rgba(124,58,237,0.15)',
                border: '1px solid rgba(124,58,237,0.35)',
                mb: 3,
              }}
            >
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  background: '#3aed3a',
                  animation: 'pulse-glow 2s ease-in-out infinite',
                  boxShadow: '0 0 8px #3aed3a',
                }}
              />
              <Typography
                variant='body2'
                sx={{
                  color: '#C4B5FD',
                  fontWeight: 600,
                  letterSpacing: 1,
                  fontSize: '0.8rem',
                }}
              >
                AVAILABLE FOR PROJECTS
              </Typography>
            </Box>
          </motion.div>

          {/* Main headline */}
          <motion.div variants={itemVariants}>
            <Typography
              variant='h1'
              sx={{
                fontSize: {
                  xs: '2.5rem',
                  sm: '3.5rem',
                  md: '4.5rem',
                  lg: '5.2rem',
                },
                lineHeight: 1.1,
                fontWeight: 900,
                mb: 2,
                maxWidth: '820px',
              }}
            >
              I Build{' '}
              <Box component='span' className='gradient-text'>
                Digital Experiences
              </Box>
              <br />
              That{' '}
              <Box component='span' sx={{ color: '#F59E0B' }}>
                Convert
              </Box>
            </Typography>
          </motion.div>

          {/* Subtext */}
          <motion.div variants={itemVariants}>
            <Typography
              variant='body1'
              sx={{
                fontSize: { xs: '1rem', md: '1.2rem' },
                color: 'rgba(255,255,255,0.65)',
                maxWidth: '600px',
                lineHeight: 1.8,
                mb: 4,
              }}
            >
              Freelance web developer specialising in high-performance React
              applications, bespoke small business websites, and
              conversion-optimised landing pages. From concept to launch — I
              make it happen.
            </Typography>
          </motion.div>

          {/* CTA Buttons */}
          <motion.div variants={itemVariants}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Button
                variant='contained'
                size='large'
                endIcon={<ArrowForwardIcon />}
                onClick={() => handleScroll('#portfolio')}
                sx={{
                  background: 'linear-gradient(135deg, #7C3AED, #5B21B6)',
                  color: '#fff',
                  px: 4,
                  py: 1.75,
                  fontSize: '1rem',
                  fontWeight: 700,
                  boxShadow: '0 8px 30px rgba(124,58,237,0.45)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #9D5AF5, #7C3AED)',
                    boxShadow: '0 12px 40px rgba(124,58,237,0.65)',
                    transform: 'translateY(-3px)',
                  },
                }}
              >
                View Examples
              </Button>
              <Button
                variant='outlined'
                size='large'
                startIcon={<MailOutlineIcon />}
                onClick={() => handleScroll('#contact')}
                sx={{
                  borderColor: 'rgba(255,255,255,0.25)',
                  color: '#fff',
                  px: 4,
                  py: 1.75,
                  fontSize: '1rem',
                  backdropFilter: 'blur(10px)',
                  background: 'rgba(255,255,255,0.05)',
                  '&:hover': {
                    borderColor: '#7C3AED',
                    background: 'rgba(124,58,237,0.1)',
                    transform: 'translateY(-3px)',
                  },
                }}
              >
                Get In Touch
              </Button>
            </Stack>
          </motion.div>

          {/* Credibility highlights */}
          <motion.div variants={itemVariants}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 3,
                mt: 6,
                flexWrap: 'wrap',
              }}
            >
              {[
                { value: 'Independent', label: 'Studio' },
                { value: '10+ Years', label: 'Experience' },
                { value: 'Now Taking', label: 'Bookings' },
              ].map((stat) => (
                <Box
                  key={stat.label}
                  sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}
                >
                  <Typography
                    variant='h5'
                    sx={{
                      fontFamily: 'Poppins',
                      fontWeight: 800,
                      fontSize: { xs: '1.05rem', md: '1.2rem' },
                      background: 'linear-gradient(135deg, #7C3AED, #06B6D4)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                    }}
                  >
                    {stat.value}
                  </Typography>
                  <Typography
                    variant='body2'
                    sx={{ color: 'rgba(255,255,255,0.55)', fontWeight: 500 }}
                  >
                    {stat.label}
                  </Typography>
                </Box>
              ))}
            </Box>
          </motion.div>
        </motion.div>
      </Container>

      {/* Bottom fade */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: 120,
          background: 'linear-gradient(to bottom, transparent, #0F0F1A)',
          pointerEvents: 'none',
        }}
      />
    </Box>
  );
}
